import { characterName, isZombie, type CharacterView } from '../lib/characters';

const th = 'px-2 py-1 text-left';
const td = 'px-2 py-1';

const when = (ms: number | null | undefined) => (ms ? new Date(ms).toLocaleString() : '-');

/** Alive (cyan) or zombie (the Dead-green plate, formaldehyde); frozen adds a grey plate. */
export function CharacterStatusPlate({ c }: { c: Pick<CharacterView, 'status' | 'frozen'> }) {
  const zombie = isZombie(c.status);
  return (
    <>
      <span className={`rounded px-1 font-semibold ${zombie ? 'bg-formaldehyde' : 'bg-nonphoto'}`}>
        {zombie ? 'zombie' : 'alive'}
      </span>
      {c.frozen && (
        <span
          className="ml-1 rounded bg-newsprint px-1 font-semibold"
          title="Above the slot cap (the VIP pass lapsed): selectable only to delete"
        >
          frozen
        </span>
      )}
    </>
  );
}

/**
 * The player's characters (ADR 0069), at the top of the detail: one row per slot. Clicking a row selects that
 * character; every run-scoped section below then reads its run. The active one (the phone's pointer) is starred.
 */
export function PlayerCharactersTable({
  characters,
  selectedRunId,
  onSelect,
}: {
  characters: CharacterView[];
  selectedRunId?: string;
  onSelect: (runId: string) => void;
}) {
  return (
    <section
      aria-label="Characters"
      className="mb-4 overflow-x-auto rounded border-2 border-ink bg-white p-4"
    >
      <h2 className="font-semibold">Characters</h2>
      {characters.length === 0 ? (
        <p className="text-sm text-bruise">No characters.</p>
      ) : (
        <table className="mt-2 w-full text-xs">
          <thead className="text-bruise">
            <tr>
              <th className={th}>Slot</th>
              <th className={th}>Run</th>
              <th className={th}>Name</th>
              <th className={th}>Status</th>
              <th className={th}>Days</th>
              <th className={th}>HP</th>
              <th className={th}>Resurrections</th>
              <th className={th}>Created</th>
              <th className={th}>Died</th>
              <th className={th}>Active</th>
            </tr>
          </thead>
          <tbody>
            {characters.map((c) => {
              const selected = c.runId === selectedRunId;
              return (
                <tr
                  key={c.runId}
                  aria-selected={selected}
                  onClick={() => onSelect(c.runId)}
                  className={`cursor-pointer border-t border-newsprint align-top hover:bg-caption/20 ${
                    selected ? 'bg-caption/40' : ''
                  }`}
                >
                  <td className={td}>
                    <button
                      type="button"
                      className="font-semibold underline"
                      aria-label={`Show ${characterName(c)}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        onSelect(c.runId);
                      }}
                    >
                      {c.slot}
                    </button>
                  </td>
                  <td className={`${td} font-mono`}>#{c.number}</td>
                  <td className={td}>{characterName(c)}</td>
                  <td className={td}>
                    <CharacterStatusPlate c={c} />
                    {c.causeOfDeath ? ` (${c.causeOfDeath})` : ''}
                  </td>
                  <td className={td}>{c.daysSurvived}</td>
                  <td className={td}>
                    {c.health}/{c.maxHealth}
                  </td>
                  <td className={td}>
                    {c.resurrectionCount}
                    {c.resurrectionCount > 0 && (
                      <span className="ml-1 rounded bg-caption px-1 font-semibold">assisted</span>
                    )}
                  </td>
                  <td className={`${td} font-mono`}>{when(c.startedAtMs)}</td>
                  <td className={`${td} font-mono`}>{when(c.diedAtMs)}</td>
                  <td className={td} title={c.active ? 'Active on the phone' : undefined}>
                    {c.active ? '★' : ''}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </section>
  );
}
